import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  Menu,
  MenuItem,
  IconButton,
  Avatar,
  Divider,
  Select,
  FormControl
} from '@mui/material';
import { 
  Schedule, 
  People, 
  Dashboard, 
  AccountCircle, 
  Logout, 
  AdminPanelSettings,
  Settings
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin, isAuthenticated } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const [anchorEl, setAnchorEl] = useState(null);
  const [langMenuEl, setLangMenuEl] = useState(null);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleMenuClose();
  };

  const handleProfile = () => {
    navigate('/profile');
    handleMenuClose();
  };

  const handleAdminPanel = () => {
    navigate('/admin');
    handleMenuClose();
  };

  const handleLangMenuOpen = (event) => {
    setLangMenuEl(event.currentTarget);
  };

  const handleLangMenuClose = () => {
    setLangMenuEl(null);
  };

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    handleLangMenuClose();
  };

  // Navigation items based on user role
  const getNavItems = () => {
    if (!isAuthenticated) return [];

    if (isAdmin()) {
      return [
        { path: '/app', label: t('navbar.chatSetup'), icon: <AdminPanelSettings /> },
        { path: '/app/schedule', label: t('navbar.scheduleView'), icon: <Schedule /> },
        { path: '/app/constraints', label: t('navbar.constraints'), icon: <Settings /> },
      ];
    } else {
      return [
        { path: '/app/constraints', label: t('navbar.constraints'), icon: <Settings /> },
      ];
    }
  };

  const navItems = getNavItems();

  return (
    <AppBar position="static" elevation={2}>
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <Typography
            variant="h6"
            component="div"
            sx={{ flexGrow: 0, mr: 4, fontWeight: 700, cursor: 'pointer' }}
            onClick={() => navigate(location.pathname.startsWith('/app') ? '/app' : '/')}
          >
            {t('navbar.title')}
          </Typography>
          
          {isAuthenticated && (
            <Box sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
              {navItems.map((item) => (
                <Button
                  key={item.path}
                  color="inherit"
                  startIcon={item.icon}
                  onClick={() => navigate(item.path)}
                  sx={{
                    backgroundColor: location.pathname === item.path ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          {isAuthenticated && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Language Switch */}
              <FormControl size="small" sx={{ minWidth: 80, mr: 1 }}>
                <Select
                  value={language}
                  onChange={(e) => handleLanguageChange(e.target.value)}
                  sx={{
                    color: 'inherit',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.23)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    },
                    '& .MuiSvgIcon-root': {
                      color: 'inherit',
                    },
                  }}
                >
                  <MenuItem value="en">🇬🇧 EN</MenuItem>
                  <MenuItem value="de">🇩🇪 DE</MenuItem>
                </Select>
              </FormControl>

              <Typography variant="body2" sx={{ mr: 1 }}>
                {user?.username}
              </Typography>
              <IconButton
                size="large"
                onClick={handleMenuOpen}
                color="inherit"
              >
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                  {user?.username?.charAt(0).toUpperCase()}
                </Avatar>
              </IconButton>
              
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem onClick={handleProfile}>
                  <AccountCircle sx={{ mr: 1 }} />
                  {t('navbar.profile')}
                </MenuItem>
                
                {isAdmin() && (
                  <MenuItem onClick={handleAdminPanel}>
                    <AdminPanelSettings sx={{ mr: 1 }} />
                    {t('navbar.adminPanel')}
                  </MenuItem>
                )}
                
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <Logout sx={{ mr: 1 }} />
                  {t('navbar.logout')}
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar; 